import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, X, Loader2 } from "lucide-react";
import client from "@/lib/api";

/**
 * Site-wide search overlay. Triggered by the search icon in the Header.
 * Debounced 220ms; results grouped by kind (Products / Accessories / Journal /
 * FAQs / Information). Each item is a clickable link that closes the overlay
 * and navigates to the resource. Keyboard-friendly: Esc closes, focus is
 * trapped to the input, hits are arrow-key navigable.
 */
export default function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setQ("");
      setData(null);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!open) return;
    if (debounced.length < 2) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    let cancelled = false;
    // Fire keyword + AI in parallel — AI runs ~1-3s, keyword <100ms. UI shows
    // keyword instantly, then merges in AI picks when they land.
    const kw = client.get(`/search?q=${encodeURIComponent(debounced)}&limit=6`).then((r) => r.data).catch(() => ({ groups: [], total: 0 }));
    const ai = client.get(`/ai-search?q=${encodeURIComponent(debounced)}&limit=6`).then((r) => r.data).catch(() => null);
    kw.then((kwData) => {
      if (cancelled) return;
      setData(kwData);
    });
    Promise.all([kw, ai]).then(([kwData, aiData]) => {
      if (cancelled) return;
      if (!aiData || !aiData.items?.length) { setLoading(false); return; }
      // Prepend an AI group at the top of the results
      const aiGroup = {
        key: "ai",
        label: aiData.reasoning ? `Best matches · ${aiData.reasoning}` : "Best matches (AI)",
        items: aiData.items.map((it) => ({ ...it, meta: "AI pick" })),
      };
      setData({
        ...kwData,
        total: (kwData?.total || 0) + aiGroup.items.length,
        groups: [aiGroup, ...(kwData?.groups || [])],
      });
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [debounced, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const go = (url) => {
    onClose();
    navigate(url);
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="search-overlay">
      <div className="max-w-2xl mx-auto mt-20 mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="bg-white shadow-2xl">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-black/10">
            <Search size={20} strokeWidth={1.5} className="text-black/40 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ask anything — 'oversized tee for tall guys', 'something for a runner'…"
              className="flex-1 bg-transparent text-base focus:outline-none placeholder:text-black/30"
              data-testid="search-input"
            />
            {loading && <Loader2 size={16} className="text-black/40 animate-spin" />}
            <button onClick={onClose} aria-label="Close search" className="text-black/40 hover:text-black" data-testid="search-close">
              <X size={20} strokeWidth={1.5} />
            </button>
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {q.length === 0 && (
              <div className="px-5 py-10 text-center text-sm text-black/45" data-testid="search-prompt">
                Type at least 2 characters to search the entire site.
              </div>
            )}
            {q.length > 0 && q.length < 2 && (
              <div className="px-5 py-6 text-center text-xs text-black/45">Keep typing…</div>
            )}
            {data && data.total === 0 && (
              <div className="px-5 py-10 text-center" data-testid="search-empty">
                <div className="label-eyebrow text-black/40">No matches</div>
                <p className="text-sm text-black/55 mt-2">
                  Nothing for <strong>"{debounced}"</strong>. Try a different spelling or browse the <Link to="/shop" onClick={onClose} className="moto-accent border-b border-[#FF3B30]">shop</Link>.
                </p>
              </div>
            )}
            {data && data.groups?.map((g) => (
              <div key={g.key} className="border-b border-black/10 last:border-b-0" data-testid={`search-group-${g.key}`}>
                <div className="px-5 pt-4 pb-2 label-eyebrow text-black/40 bg-[#FAFAFA] sticky top-0">{g.label}</div>
                {g.items.map((it) => (
                  <button
                    key={`${g.key}-${it.id}`}
                    onClick={() => go(it.url)}
                    className="w-full flex gap-4 px-5 py-3 hover:bg-[#FFF5F4] transition-colors text-left border-t border-black/5"
                    data-testid={`search-hit-${g.key}-${it.id}`}
                  >
                    {it.image && (
                      <img src={it.image} alt="" className="w-12 h-12 object-cover shrink-0 bg-[#FAFAFA]" loading="lazy" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium line-clamp-1">{it.title}</div>
                      {it.snippet && (
                        <div className="text-xs text-black/55 mt-0.5 line-clamp-2">{it.snippet}</div>
                      )}
                      {it.meta && (
                        <div className="text-[10px] uppercase tracking-wider text-black/35 mt-1">{it.meta}</div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
